/**
 * Dependency Form Component
 *
 * Form for adding or editing service dependencies
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { LIST_SERVICES } from '../../graphql/queries';
import type { ServiceConnection } from '../../graphql/types';
import type { CreateDependencyInput, UpdateDependencyInput } from '../../lib/apiClient';
import type { AutocompleteFilterCallback, IAutocompleteComponent } from '@tylertech/forge';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/label';
import '@tylertech/forge/autocomplete';
import './DependencyForm.css';

/** Max services fetched per keystroke — server-side paged search, not a client-loaded full list (issue #652). */
const SERVICE_SEARCH_LIMIT = 20;

/**
 * Props for DependencyForm
 */
export interface DependencyFormProps {
  /** Current service (the "from" service) */
  currentServiceId: string;
  /**
   * Ids to exclude from the target-service picker in addition to
   * `currentServiceId` — typically the toServiceId of every dependency the
   * current service already has, since re-adding one hits the backend's
   * 409 duplicate check anyway (issue #652).
   */
  excludeServiceIds?: string[];
  /** Existing dependency to edit (if editing) */
  existingDependency?: {
    toServiceId: string;
    dependencyType: string;
    communicationPattern: string;
    criticality?: string | null;
    notes?: string | null;
  };
  /** Callback when form is submitted */
  onSubmit: (data: CreateDependencyInput | UpdateDependencyInput) => void | Promise<void>;
  /** Callback when form is cancelled */
  onCancel: () => void;
  /** Whether the form is in loading state */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * DependencyForm Component
 */
export function DependencyForm({
  currentServiceId,
  excludeServiceIds = [],
  existingDependency,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
}: DependencyFormProps): JSX.Element {
  const isEditMode = !!existingDependency;
  const ForgeButton = 'forge-button' as React.ElementType;
  const ForgeAutocomplete = 'forge-autocomplete' as React.ElementType;
  const ForgeTextField = 'forge-text-field' as React.ElementType;

  // Form state
  const [toServiceId, setToServiceId] = useState(existingDependency?.toServiceId || '');
  const [dependencyType, setDependencyType] = useState(
    existingDependency?.dependencyType || 'hard'
  );
  const [communicationPattern, setCommunicationPattern] = useState(
    existingDependency?.communicationPattern || 'REST'
  );
  const [criticality, setCriticality] = useState(existingDependency?.criticality || 'medium');
  const [notes, setNotes] = useState(existingDependency?.notes || '');

  // Target-service autocomplete (issue #652): server-side paged search via
  // `services(filter:{search})`, never a client-loaded full list.
  const autocompleteRef = useRef<(HTMLElement & Partial<IAutocompleteComponent>) | null>(null);
  const [fetchServiceOptions] = useLazyQuery<{ services: ServiceConnection }>(LIST_SERVICES, {
    fetchPolicy: 'network-only',
  });

  const excludedServiceIds = useMemo(
    () => new Set([currentServiceId, ...excludeServiceIds]),
    [currentServiceId, excludeServiceIds]
  );

  const filterServices = useCallback<AutocompleteFilterCallback<string>>(
    async (filterText) => {
      const search = filterText.trim();
      const { data } = await fetchServiceOptions({
        variables: {
          first: SERVICE_SEARCH_LIMIT,
          filter: search ? { search } : undefined,
        },
      });
      const edges = data?.services.edges ?? [];
      return edges
        .filter((edge) => !excludedServiceIds.has(edge.node.entityId))
        .map((edge) => ({
          value: edge.node.entityId,
          label: `${edge.node.name} (${edge.node.serviceType})`,
        }));
    },
    [fetchServiceOptions, excludedServiceIds]
  );

  useEffect(() => {
    const el = autocompleteRef.current;
    if (!el) return;
    el.filter = filterServices;
  }, [filterServices]);

  useEffect(() => {
    const el = autocompleteRef.current;
    if (!el) return;
    const handleAutocompleteChange = (e: Event): void => {
      const value = (e as CustomEvent<string | null>).detail;
      setToServiceId(value ?? '');
    };
    el.addEventListener('forge-autocomplete-change', handleAutocompleteChange);
    return () => el.removeEventListener('forge-autocomplete-change', handleAutocompleteChange);
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (isEditMode) {
      // Edit mode - only send changed fields
      const updates: UpdateDependencyInput = {
        dependencyType: dependencyType as 'hard' | 'soft',
        communicationPattern: communicationPattern as UpdateDependencyInput['communicationPattern'],
        criticality: criticality as UpdateDependencyInput['criticality'],
        notes: notes || undefined,
      };
      await onSubmit(updates);
    } else {
      // Create mode - send all required fields
      // fromServiceId is the current service (the one that HAS the dependency)
      // toServiceId is the service being depended upon
      const data: CreateDependencyInput = {
        fromServiceId: currentServiceId,
        toServiceId,
        dependencyType: dependencyType as 'hard' | 'soft',
        communicationPattern: communicationPattern as CreateDependencyInput['communicationPattern'],
        criticality: criticality as CreateDependencyInput['criticality'],
        notes: notes || undefined,
      };
      await onSubmit(data);
    }
  };

  const isFormValid = !isEditMode ? toServiceId && dependencyType && communicationPattern : true;

  return (
    <div className="dependency-form">
      <h3 className="dependency-form__title">
        {isEditMode ? 'Edit Dependency' : 'Add New Dependency'}
      </h3>

      {error && <div className="dependency-form__error">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Target Service (only in create mode) — filterable Forge autocomplete, issue #652 */}
        {!isEditMode && (
          <div className="dependency-form__field">
            <label htmlFor="toService" className="dependency-form__label">
              This service depends on *
            </label>
            <ForgeAutocomplete
              ref={autocompleteRef as React.Ref<unknown>}
              filterOnFocus
              debounce={300}
              emptyMessage="No matching services"
              data-testid="dependency-target-service-autocomplete"
            >
              <ForgeTextField>
                <input
                  id="toService"
                  type="text"
                  autoComplete="off"
                  placeholder="Search services..."
                  disabled={loading}
                  required
                  aria-label="This service depends on"
                  data-testid="dependency-target-service-input"
                />
              </ForgeTextField>
            </ForgeAutocomplete>
            <div className="dependency-form__hint">
              Select the service that the current service needs to function
            </div>
          </div>
        )}

        {/* Dependency Type */}
        <div className="dependency-form__field">
          <label htmlFor="dependencyType" className="dependency-form__label">
            Dependency Type *
          </label>
          <select
            id="dependencyType"
            value={dependencyType}
            onChange={(e) => setDependencyType(e.target.value)}
            disabled={loading}
            required
            className="dependency-form__control"
          >
            <option value="hard">Hard - Service cannot function without this dependency</option>
            <option value="soft">Soft - Service can degrade gracefully</option>
          </select>
        </div>

        {/* Communication Pattern */}
        <div className="dependency-form__field">
          <label htmlFor="communicationPattern" className="dependency-form__label">
            Communication Pattern *
          </label>
          <select
            id="communicationPattern"
            value={communicationPattern}
            onChange={(e) => setCommunicationPattern(e.target.value)}
            disabled={loading}
            required
            className="dependency-form__control"
          >
            <option value="REST">REST API</option>
            <option value="GraphQL">GraphQL</option>
            <option value="gRPC">gRPC</option>
            <option value="Async">Async</option>
            <option value="EventDriven">Event-Driven</option>
            <option value="MessageQueue">Message Queue</option>
            {/* Ingestion-only value (issue #1320) — materialized when a descriptor
                omits communicationPattern; selectable here so editing such a
                dependency doesn't silently fall back to an unmatched option. */}
            <option value="Unknown">Unknown</option>
          </select>
        </div>

        {/* Criticality */}
        <div className="dependency-form__field">
          <label htmlFor="criticality" className="dependency-form__label">
            Criticality
          </label>
          <select
            id="criticality"
            value={criticality}
            onChange={(e) => setCriticality(e.target.value)}
            disabled={loading}
            className="dependency-form__control"
          >
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        {/* Notes */}
        <div className="dependency-form__field">
          <label htmlFor="notes" className="dependency-form__label">
            Notes
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={loading}
            rows={3}
            placeholder="Additional notes about this dependency..."
            className="dependency-form__control dependency-form__control--textarea"
          />
        </div>

        <div className="dependency-form__actions">
          <ForgeButton type="button" variant="outlined" onClick={onCancel} disabled={loading}>
            Cancel
          </ForgeButton>
          <ForgeButton type="submit" variant="raised" disabled={!isFormValid || loading}>
            {loading ? 'Saving...' : isEditMode ? 'Update Dependency' : 'Add Dependency'}
          </ForgeButton>
        </div>
      </form>
    </div>
  );
}
